import React, {
  useImperativeHandle,
  forwardRef,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
// @ts-ignore: CSS side-effect import without type declarations
import 'react-toastify/dist/ReactToastify.css';

import Editor from './Editor';
import { generateThumbnail } from './thumbnail';

import {
  createUserDefinedTemplate,
  updateUserDefinedTemplate,
  updateTemplateMetadata,
  updatePrebuiltTemplateMetadata,
  TemplatePayload,
  fetchTemplateById,
} from '../../features/templates/templateService';

import SaveTemplateModal from './SaveTemplateModal';
import EditableField from '../../components/EditableField';
import { FiX, FiCheck } from 'react-icons/fi';

import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../app/store';
import { clearCurrentTemplateId } from '../../features/templates/templatesSlice';

const FiXComponent = FiX as any;
const FiCheckComponent = FiCheck as any;

export interface Template {
  id: string;
  name: string;
  html: string;
  css: string;
  createdAt: string | number;
  thumbnail?: string | null;
  isPrebuilt?: boolean;
  isUserDefined?: boolean;
  status?: string;
  category?: string;
  version?: number;
  orgId?: string;
  description?: string;
  folderId?: string;
  assetId?: string | null;
  metadata?: any;
  capability?: any;
  createdBy?: string;
  updatedAt?: string;
}

interface EditorPageProps {
  fromTab2: string | undefined;
}

export interface EditorPageHandle {
  handleSave: () => void;
  handlePreview: () => void;
}

const unescapeString = (str: string): string => {
  if (!str) return '';
  return str.replace(/\\n/g, '\n').replace(/\\"/g, '"').replace(/\\t/g, '\t');
};

const extractHtmlFromBlocks = (blocks: any[] = []): string => {
  const rawBlock = blocks.find((b) => b.type === 'rawHtml');
  return rawBlock ? unescapeString(rawBlock.text || '') : '';
};

const EditorPage = forwardRef<EditorPageHandle, EditorPageProps>(
  (props, ref) => {
    const templateId = localStorage.getItem('currentTemplateId');
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [template, setTemplate] = useState<Template | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [hasChanges, setHasChanges] = useState(false);

    const editorRef = React.useRef<any>(null);

    const [saveModalOpen, setSaveModalOpen] = useState(false);
    const [pendingSaveData, setPendingSaveData] = useState<any>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [saveascopyinput, showSaveascopyinput] = useState(false);

    const [isRefetching, setIsRefetching] = useState(false);
    // const [editingTagIndex, setEditingTagIndex] = useState<number | null>(null);
    const [newTagValue, setNewTagValue] = useState<string>('');
    const [isAddingTag, setIsAddingTag] = useState<boolean>(false);
    const [isUpdatingTags, setIsUpdatingTags] = useState<boolean>(false);

    const updateInProgressRef = useRef<boolean>(false);
    const lastUpdateRef = useRef<{
      field: 'name' | 'description';
      value: string;
      timestamp: number;
    } | null>(null);

    const initialTemplateRef = useRef<Template | null>(null);

    const userDefinedTemplates = useSelector(
      (state: RootState) => state.templates.userDefinedTemplates
    );

    const currentTags: string[] = template?.metadata?.tags || [];
    const isPrebuilt = props.fromTab2 === 'templatesGallery';

    useEffect(() => {
      const loadTemplate = async () => {
        if (!templateId) {
          setTemplate(null);
          setLoading(false);
          return;
        }

        setLoading(true);
        setError(null);

        try {
          const response = await fetchTemplateById(props?.fromTab2, templateId);

          const isPrebuiltTemplate = props?.fromTab2 === 'templatesGallery';

          const transformedTemplate: Template = {
            id: response.id,
            name: response.name || 'Untitled Template',
            html: extractHtmlFromBlocks(response.blocks),
            css: unescapeString(response.styles || ''),
            createdAt: response.createdAt || new Date().toISOString(),
            thumbnail: response.thumbnailUrl || null,
            isPrebuilt: isPrebuiltTemplate,
            isUserDefined: !isPrebuiltTemplate,
            status: response.status || 'DRAFT',
            category: response.metadata?.category || '',
            version: response.version || 0,
            orgId: response.orgId,
            description: response.description || '',
            folderId: response.folderId || 'folder_001',
            assetId: response.assetId || null,
            metadata: response.metadata || {},
            capability: response.capability || null,
            createdBy: response.createdBy,
            updatedAt: response.updatedAt || response.createdAt,
          };

          setTemplate(transformedTemplate);
          initialTemplateRef.current = JSON.parse(JSON.stringify(transformedTemplate));
          setHasChanges(false);
        } catch (err: any) {
          // console.error('Failed to load template:', err);
          setError(err.message || 'Failed to load template');
          toast.error('Could not load template. Please try again.', {
            autoClose: 5000,
          });
        } finally {
          setLoading(false);
        }
      };

      loadTemplate();
    }, [templateId]);

    const checkForChanges = useCallback(() => {
      if (!template || !initialTemplateRef.current) {
        return false;
      }

      if (!isPrebuilt && template.name !== initialTemplateRef.current.name) {
        return true;
      }

      if (!isPrebuilt && template.description !== initialTemplateRef.current.description) {
        return true;
      }

      const currentTagsStr = JSON.stringify(template.metadata?.tags || []);
      const initialTagsStr = JSON.stringify(initialTemplateRef.current.metadata?.tags || []);
      if (currentTagsStr !== initialTagsStr) {
        return true;
      }

      return false;
    }, [template, isPrebuilt]);

    const handleContentChange = useCallback((hasEditorChanges: boolean) => {
      const hasMetadataChanges = checkForChanges();
      setHasChanges(hasEditorChanges || hasMetadataChanges);
    }, [checkForChanges]);

    useEffect(() => {
      if (template && initialTemplateRef.current) {
        const changed = checkForChanges();
        setHasChanges(prev => prev || changed);
      }
    }, [template, checkForChanges]);

    // Function to check if there are any changes (including editor content)
    const checkAllChanges = useCallback(() => {
      // Check metadata changes
      const hasMetadataChanges = checkForChanges();

      // Check editor content changes
      let hasEditorChanges = false;
      if (editorRef.current && editorRef.current.checkContentChanges) {
        hasEditorChanges = editorRef.current.checkContentChanges() || false;
      }

      return hasMetadataChanges || hasEditorChanges;
    }, [checkForChanges]);

    const handleUpdateTags = async (newTags: string[]) => {
      if (!template?.id) return;

      setIsUpdatingTags(true);
      try {
        if (isPrebuilt) {
          const updatedMetadata = {
            ...template.metadata,
            tags: newTags
          };
          setTemplate({
            ...template,
            metadata: updatedMetadata
          });
          setHasChanges(true);

          setIsUpdatingTags(false);
          // setEditingTagIndex(null);
          setIsAddingTag(false);
          setNewTagValue('');
          return;
        }

        const updatedMetadata = {
          ...template.metadata,
          tags: newTags
        };

        await updateTemplateMetadata(template.id, {
          metadata: updatedMetadata
        });

        setTemplate({
          ...template,
          metadata: updatedMetadata
        });
        setHasChanges(true);

        toast.success('Tags updated successfully!', {
          autoClose: 2000,
        });

        const response = await fetchTemplateById(props.fromTab2, template.id);
        const transformedTemplate: Template = {
          id: response.id,
          name: response.name || 'Untitled Template',
          html: extractHtmlFromBlocks(response.blocks),
          css: unescapeString(response.styles || ''),
          createdAt: response.createdAt || new Date().toISOString(),
          thumbnail: response.thumbnailUrl || null,
          isPrebuilt: false,
          isUserDefined: true,
          status: response.status || '',
          category: response.metadata?.category || 'Marketing',
          version: response.version || 0,
          orgId: response.orgId,
          description: response.description || '',
          folderId: response.folderId || 'folder_001',
          assetId: response.assetId || null,
          metadata: response.metadata || {},
          capability: response.capability || null,
          createdBy: response.createdBy,
          updatedAt: response.updatedAt || response.createdAt,
        };
        setTemplate(transformedTemplate);
        initialTemplateRef.current = JSON.parse(JSON.stringify(transformedTemplate));
        setHasChanges(false);

      } catch (err: any) {
        // console.error('Failed to update tags:', err);
        toast.error(err.message || 'Failed to update tags');
      } finally {
        setIsUpdatingTags(false);
        // setEditingTagIndex(null);
        setIsAddingTag(false);
        setNewTagValue('');
      }
    };

    const handleAddTag = () => {
      if (isAddingTag) return;
      setIsAddingTag(true);
      setNewTagValue('');
    };

    const handleConfirmAddTag = async () => {
      const trimmedTag = newTagValue.trim();
      if (!trimmedTag) {
        setIsAddingTag(false);
        setNewTagValue('');
        return;
      }

      if (currentTags.includes(trimmedTag)) {
        toast.warning('Tag already exists');
        return;
      }

      const updatedTags = [...currentTags, trimmedTag];
      await handleUpdateTags(updatedTags);
    };

    const handleCancelAddTag = () => {
      setIsAddingTag(false);
      setNewTagValue('');
    };

    const handleRemoveTag = async (index: number) => {
      const updatedTags = currentTags.filter((_: string, i: number) => i !== index);
      await handleUpdateTags(updatedTags);
    };

    const handleUpdateMetadata = useCallback(
      async (field: 'name' | 'description', value: string) => {
        if (!template?.id) return;

        if (updateInProgressRef.current) {
          return;
        }

        const now = Date.now();
        if (
          lastUpdateRef.current &&
          lastUpdateRef.current.field === field &&
          lastUpdateRef.current.value === value &&
          now - lastUpdateRef.current.timestamp < 2000
        ) {
          return;
        }

        updateInProgressRef.current = true;
        lastUpdateRef.current = { field, value, timestamp: now };

        setIsRefetching(true);

        try {
          if (template.orgId === 'test-org') {
            await updatePrebuiltTemplateMetadata(template.id, {
              [field]: value,
            });
          } else {
            await updateTemplateMetadata(template.id, { [field]: value });
          }

          toast.success(
            `${field === 'name' ? 'Template name' : 'Description'} updated successfully!`,
            {
              autoClose: 3000,
              toastId: `metadata-update-${template.id}-${field}`,
            }
          );

          const response = await fetchTemplateById(props.fromTab2, template.id);

          const transformedTemplate: Template = {
            id: response.id,
            name: response.name || 'Untitled Template',
            html: extractHtmlFromBlocks(response.blocks),
            css: unescapeString(response.styles || ''),
            createdAt: response.createdAt || new Date().toISOString(),
            thumbnail: response.thumbnailUrl || null,
            isPrebuilt: false,
            isUserDefined: true,
            status: response.status || '',
            category: response.metadata?.category || 'Marketing',
            version: response.version || 0,
            orgId: response.orgId,
            description: response.description || '',
            folderId: response.folderId || 'folder_001',
            assetId: response.assetId || null,
            metadata: response.metadata || {},
            capability: response.capability || null,
            createdBy: response.createdBy,
            updatedAt: response.updatedAt || response.createdAt,
          };

          setTemplate(transformedTemplate);
          initialTemplateRef.current = JSON.parse(JSON.stringify(transformedTemplate));
          setHasChanges(false);
        } catch (err: any) {
          // console.error(`Failed to update ${field}:`, err);
          toast.error(err.message || `Failed to update ${field}`, {
            toastId: `metadata-error-${template.id}-${field}`,
          });
        } finally {
          updateInProgressRef.current = false;
          setIsRefetching(false);
        }
      },
      [template?.id, template?.orgId]
    );

    const debouncedUpdateMetadata = useCallback(
      async (field: 'name' | 'description', value: string) => {
        return handleUpdateMetadata(field, value);
      },
      [handleUpdateMetadata]
    );

    // This function is called from the Editor component
    const handleSave = async ({
      html,
      css,
      blocks,
      styles,
      thumbnail,
    }: {
      html: string;
      css: string;
      blocks: any[];
      styles: string;
      thumbnail?: string | null;
    }) => {

      const isUpdate = !!template?.id;
      const isSavingCopy = saveascopyinput || isPrebuilt;

      // Check for changes directly
      const hasAnyChanges = checkAllChanges();

      // For prebuilt templates, always require changes
      if (isPrebuilt && !hasAnyChanges) {
        toast.info('No changes detected. Make some changes to save a copy of this template.', {
          autoClose: 3000,
        });
        return;
      }

      // For user templates, if no changes and not saving as copy, prevent save
      if (isUpdate && !isSavingCopy && !hasAnyChanges) {
        toast.info('No changes detected. Make some changes to save the template.', {
          autoClose: 3000,
        });
        return;
      }

      let thumb = thumbnail;
      if (!thumb) {
        try {
          thumb = await generateThumbnail({ html, css });
        } catch (e) {
          thumb = null;
        }
      }

      const saveData = { html, css, blocks, styles, thumbnail: thumb };
      setPendingSaveData(saveData);
      setSaveModalOpen(true);
    };

    const performSave = async (name: string) => {


      // If pendingSaveData is null, try to get data directly from editor
      let saveData = pendingSaveData;
      if (!saveData && editorRef.current) {
        const html = editorRef.current.getHtml?.() || '';
        const css = editorRef.current.getCss?.() || '';
        let bodyContent = html;
        const bodyRegex = /<body[^>]*>([\s\S]*)<\/body>/i;
        const bodyMatch = bodyContent.match(bodyRegex);
        if (bodyMatch && bodyMatch[1]) {
          bodyContent = bodyMatch[1].trim();
        }
        const blocks = [{ type: 'rawHtml', text: bodyContent }];
        let thumbnail = null;
        try {
          thumbnail = await generateThumbnail({ html, css });
        } catch (e) {
          thumbnail = null;
        }
        saveData = { html, css, blocks, styles: css, thumbnail };
        setPendingSaveData(saveData);
      }

      if (!saveData) {
        // console.error('No save data available!');
        toast.error('No data to save. Please try again.', {
          autoClose: 3000,
        });
        return;
      }

      if (isSaving) return;
      setIsSaving(true);

      const { html, css, blocks, styles } = saveData;
      const now = new Date().toISOString();

      const isUpdate = !!template?.id;
      const isSavingCopy = saveascopyinput || isPrebuilt;


      let tags = template?.metadata?.tags || [];

      if (!isUpdate && !isSavingCopy) {
        const savedTags = localStorage.getItem('newTemplateTags');
        if (savedTags) {
          try {
            const parsedTags = JSON.parse(savedTags);
            if (parsedTags.length > 0) {
              tags = parsedTags;
            }
          } catch (e) {
            // console.error('Failed to parse saved tags:', e);
            toast.error('Failed to load saved tags');
          }
        }
      }

      const metadata = {
        category: template?.metadata?.category || 'Marketing',
        tags: tags,
        ownership: {
          type: 'org',
          orgId: template?.orgId || 'org_123456',
          userId: template?.createdBy || 'user_789'
        },
        createdBy: template?.createdBy || 'user_789',
        createdAt: template?.createdAt || now,
        updatedAt: now
      };

      const payload: TemplatePayload = {
        orgId: template?.orgId || 'org_123456',
        description: template?.description || '',
        status: template?.status || 'DRAFT',
        version: (template?.version || 0) + 1,
        folderId: template?.folderId || 'folder_001',
        assetId: template?.assetId || null,
        name,
        styles: styles || css,
        blocks:
          blocks.length > 0
            ? blocks
            : [
              {
                type: 'rawHtml',
                text: html,
              },
            ],
        metadata: metadata,
        capability: template?.capability || null,
        createdBy: template?.createdBy || 'user_789',
        createdAt: template?.createdAt || now,
        updatedAt: now,
        ...(isUpdate && { expectedVersion: template?.version || 0 }),
      };

      try {
        if (isSavingCopy || !isUpdate) {
          await createUserDefinedTemplate({
            payload: payload,
            files: [],
          });
          toast.success(isSavingCopy ? 'Template saved as copy successfully!' : 'Template created successfully!');
        }
        else if (isUpdate && template?.id) {
          await updateUserDefinedTemplate({
            templateId: template.id,
            payload,
            files: [],
          });
          toast.success('Template updated successfully!');
        }

        localStorage.removeItem('newTemplateTags');

        setSaveModalOpen(false);
        setPendingSaveData(null);
        showSaveascopyinput(false);
        setTimeout(() => {
          localStorage.removeItem('currentTemplateId');
          dispatch(clearCurrentTemplateId());
          navigate(`/templates?tab=yourTemplates`, {
            state: { refresh: true },
          });
        }, 800);
      } catch (err: any) {
        // console.error('Save error:', err);
        toast.error(err.message || 'Failed to save template');
      } finally {
        setIsSaving(false);
      }
    };

    const handlePreview = () => {
      editorRef.current?.handlePreview?.();
    };

    const handleEditorSave = () => {
      const isUpdate = !!template?.id;
      const isSavingCopy = saveascopyinput || isPrebuilt;

      // Check for changes directly
      const hasAnyChanges = checkAllChanges();

      // For prebuilt templates, prevent save if no changes
      if (isPrebuilt && !hasAnyChanges) {
        toast.info('No changes detected. Make some changes to save a copy of this template.', {
          autoClose: 3000,
        });
        return;
      }

      // If it's an update (not saving as copy) and no changes, prevent save
      if (isUpdate && !isSavingCopy && !hasAnyChanges) {
        toast.info('No changes detected. Make some changes to save the template.', {
          autoClose: 3000,
        });
        return;
      }

      editorRef.current?.handleSave?.();
    };

    useImperativeHandle(ref, () => ({
      handleSave: handleEditorSave,
      handlePreview,
    }));

    if (loading) {
      return (
        <div className='flex items-center justify-center h-96'>
          <div className='text-center'>
            <div className='animate-spin rounded-full h-12 w-12 border-t-4 border-b-4 border-orange-500 mx-auto'></div>
            <p className='mt-4 text-gray-600'>Loading template...</p>
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className='text-center py-12'>
          <div className='bg-red-50 border border-red-200 rounded-lg p-6 max-w-lg mx-auto'>
            <h3 className='text-red-800 font-semibold'>
              Failed to Load Template
            </h3>
            <p className='text-red-600 mt-2'>{error}</p>
            <button
              onClick={() => {
                window.location.reload();
              }}
              className='mt-4 px-6 py-2 bg-red-600 text-white rounded hover:bg-red-700'
            >
              Retry
            </button>
          </div>
        </div>
      );
    }

    return (
      <div>
        {template && (
          <div className='p-5 bg-white rounded-t-lg border-t shadow'>
            {!isPrebuilt && (
              <>
                <EditableField
                  fromTab={props?.fromTab2}
                  value={template.name}
                  template={template}
                  onSave={(v) => debouncedUpdateMetadata('name', v)}
                  fieldName='name'
                  textClassName='text-2xl font-bold text-gray-800'
                  inputClassName='text-2xl font-bold'
                  placeholder='Template Name'
                  existingNames={userDefinedTemplates.map((t) => t.name)}
                  currentId={template.id}
                />

                <EditableField
                  fromTab={props?.fromTab2}
                  value={template.description || ''}
                  onSave={(v) => debouncedUpdateMetadata('description', v)}
                  fieldName='description'
                  textClassName='text-sm text-gray-600'
                  placeholder='Add a description...'
                  type='textarea'
                  existingNames={userDefinedTemplates.map((t) => t.name)}
                  currentId={template.id}
                />
              </>
            )}

            {isPrebuilt && (
              <>
                <h2 className='text-2xl font-bold text-gray-800'>{template.name}</h2>
                {template.description && (
                  <p className='text-sm text-gray-600 mt-1'>{template.description}</p>
                )}
              </>
            )}

            <div className='flex flex-wrap gap-3 mt-3 items-center'>
              {currentTags.map((tag: string, index: number) => (
                <span
                  key={index}
                  className='pl-[11px] pr-[8px] py-[8px] bg-[#ddfafb] text-[#01a3c0] gap-2 rounded-full text-xs font-medium shadow-md flex items-center'
                >
                  {tag}
                  <span
                    className='cursor-pointer hover:text-red-500 transition-colors'
                    onClick={() => handleRemoveTag(index)}
                  >
                    <FiXComponent size={16} />
                  </span>
                </span>
              ))}

              {isAddingTag ? (
                <div className='flex items-center gap-1 bg-white border border-teal-300 rounded-full px-2 py-1 shadow-sm'>
                  <input
                    type='text'
                    value={newTagValue}
                    onChange={(e) => setNewTagValue(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleConfirmAddTag();
                      } else if (e.key === 'Escape') {
                        handleCancelAddTag();
                      }
                    }}
                    placeholder='Enter tag...'
                    className='text-xs w-24 focus:outline-none px-1 py-1'
                    autoFocus
                    disabled={isUpdatingTags}
                  />
                  <button
                    onClick={handleConfirmAddTag}
                    disabled={isUpdatingTags || !newTagValue.trim()}
                    className='p-1 text-green-600 hover:bg-green-50 rounded transition-colors disabled:opacity-50'
                    title='Add tag'
                  >
                    <FiCheckComponent size={14} />
                  </button>
                  <button
                    onClick={handleCancelAddTag}
                    disabled={isUpdatingTags}
                    className='p-1 text-red-500 hover:bg-red-50 rounded transition-colors'
                    title='Cancel'
                  >
                    <FiXComponent size={14} />
                  </button>
                </div>
              ) : (
                <span
                  className='px-3 py-[8px] bg-[#01a3c0] text-white rounded-full text-xs font-medium cursor-pointer shadow-md hover:bg-cyan-600 transition-colors'
                  onClick={handleAddTag}
                >
                  Add tag
                </span>
              )}

              {isPrebuilt && currentTags.length === 0 && !isAddingTag && (
                <span className='text-xs text-gray-400 ml-2'>
                  Tags will be saved when you "Save a Copy"
                </span>
              )}
            </div>
          </div>
        )}
        {isRefetching && (
          <div className='flex items-center gap-2 text-sm text-gray-500 mb-4'>
            <svg className='animate-spin h-4 w-4' viewBox='0 0 24 24'>
              <circle
                cx='12'
                cy='12'
                r='10'
                stroke='currentColor'
                strokeWidth='4'
                fill='none'
                className='opacity-25'
              />
              <path
                fill='currentColor'
                d='M4 12a8 8 0 018-8v8z'
                className='opacity-75'
              />
            </svg>
            Updating template info...
          </div>
        )}

        <Editor
          ref={editorRef}
          template={template || undefined}
          onSave={handleSave}
          onGenerateThumbnail={generateThumbnail}
          onContentChange={handleContentChange}
          hasChanges={hasChanges}
          isUpdate={!!templateId}
          isSavingCopy={saveascopyinput || isPrebuilt}
          isPrebuilt={isPrebuilt}
        />

        <SaveTemplateModal
          showSaveascopyinput={showSaveascopyinput}
          saveascopyinput={saveascopyinput}
          fromTab={props?.fromTab2}
          isOpen={saveModalOpen}
          onClose={() => !isSaving && setSaveModalOpen(false)}
          onSave={performSave}
          defaultName={template?.name || 'My Template'}
          existingNames={userDefinedTemplates.map((t) => t.name)}
          isSaving={isSaving}
          isUpdate={!!templateId}
          saveDisabled={false}
          isPrebuilt={isPrebuilt}
        />
      </div>
    );
  }
);

EditorPage.displayName = 'EditorPage';

export default EditorPage;